/**
 * Message File Upload Service
 * Handles file uploads for chat messages
 */

import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { storage } from '../../../services/firebase/firebase';
import { getAuth } from 'firebase/auth';

/**
 * Upload a file for a message attachment
 * 
 * @param {File} file - File to upload
 * @param {string} conversationId - Conversation ID
 * @param {string} messageId - Message ID (generated client-side)
 * @param {string} fileType - Type of file (image, document, audio)
 * @returns {Promise<Object>} Upload result with URL and metadata
 */
const uploadMessageFile = async (file, conversationId, messageId, fileType = 'document') => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('You must be logged in to upload files');
    }
    
    // Generate unique filename
    const timestamp = Date.now();
    const fileExtension = file.name.split('.').pop();
    const fileName = `${timestamp}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
    
    // Create storage reference
    const storageRef = ref(
      storage, 
      `messages/${conversationId}/${messageId}/${fileName}`
    );
    
    // Upload file
    const snapshot = await uploadBytes(storageRef, file);
    
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    // Return file metadata
    return {
      url: downloadURL,
      name: file.name,
      size: file.size,
      type: file.type,
      fileType: fileType,
      storagePath: snapshot.ref.fullPath,
      uploadedAt: new Date().toISOString(),
      uploadedBy: currentUser.uid
    };
    
  } catch (error) {
    console.error('Error uploading message file:', error);
    throw error;
  }
};

/**
 * Delete a message file from storage
 * 
 * @param {string} storagePath - Full storage path of the file
 * @returns {Promise<void>}
 */
const deleteMessageFile = async (storagePath) => {
  try {
    const storageRef = ref(storage, storagePath);
    await deleteObject(storageRef);
  } catch (error) {
    console.error('Error deleting message file:', error);
    throw error;
  }
};

/**
 * Get file type category from MIME type
 * 
 * @param {string} mimeType - File MIME type
 * @returns {string} File category (image, document, audio, other)
 */
const getFileCategory = (mimeType) => {
  if (mimeType.startsWith('image/')) {
    return 'image';
  } else if (mimeType.startsWith('audio/')) {
    return 'audio';
  } else if (
    mimeType === 'application/pdf' ||
    mimeType === 'application/msword' ||
    mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    mimeType === 'application/vnd.ms-powerpoint' ||
    mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
    mimeType === 'application/vnd.ms-excel' ||
    mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    mimeType === 'text/plain'
  ) {
    return 'document';
  } else {
    return 'other';
  }
};

/**
 * Format file size for display
 * 
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Get file icon based on file type
 * 
 * @param {string} mimeType - File MIME type
 * @returns {string} Icon name for the file type
 */
const getFileIcon = (mimeType) => {
  if (mimeType.startsWith('image/')) {
    return 'Image';
  } else if (mimeType === 'application/pdf') {
    return 'FileText';
  } else if (
    mimeType === 'application/msword' ||
    mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ) {
    return 'FileText';
  } else if (
    mimeType === 'application/vnd.ms-powerpoint' ||
    mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
  ) {
    return 'Presentation';
  } else if (
    mimeType === 'application/vnd.ms-excel' ||
    mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ) {
    return 'FileSpreadsheet';
  } else if (mimeType.startsWith('audio/')) {
    return 'Music';
  } else {
    return 'File';
  }
};

/**
 * Validate file before upload
 * 
 * @param {File} file - File to validate
 * @param {string} fileType - Expected file type category
 * @returns {Object} Validation result
 */
const validateFile = (file, fileType = 'any') => {
  const errors = [];
  
  // Check file size (10MB for documents/images, 50MB for audio)
  const maxSize = fileType === 'audio' ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
  if (file.size > maxSize) {
    errors.push(`File size must be less than ${fileType === 'audio' ? '50MB' : '10MB'}`);
  }
  
  // Check file type
  const category = getFileCategory(file.type);
  if (fileType !== 'any' && category !== fileType) {
    errors.push(`Invalid file type. Expected ${fileType} file.`);
  }
  
  // Check for potentially dangerous files
  const dangerousExtensions = ['.exe', '.bat', '.cmd', '.scr', '.com', '.pif'];
  const fileName = file.name.toLowerCase();
  const isDangerous = dangerousExtensions.some(ext => fileName.endsWith(ext));
  
  if (isDangerous) {
    errors.push('This file type is not allowed for security reasons.');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    category,
    formattedSize: formatFileSize(file.size)
  };
};

const messageFileService = {
  uploadMessageFile,
  deleteMessageFile,
  getFileCategory,
  formatFileSize,
  getFileIcon,
  validateFile
};

export default messageFileService;